import { type Static, Type } from "@sinclair/typebox";

const IdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const MoneyFenSchema = Type.Integer({ minimum: 0, maximum: 100_000_000 });
const SignedMoneyFenSchema = Type.Integer({ minimum: -100_000_000, maximum: 100_000_000 });
const QuantitySchema = Type.Integer({ minimum: 0, maximum: 1_000_000 });
const SignedQuantitySchema = Type.Integer({ minimum: -1_000_000, maximum: 1_000_000 });
const WalletAmountSchema = Type.Integer({ minimum: 1, maximum: 1_000_000_000_000 });
const SignedWalletAmountSchema = Type.Integer({
  minimum: -1_000_000_000_000,
  maximum: 1_000_000_000_000,
});

export const ProductUpsertRequestSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
    storeProductCode: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    ),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    category: Type.String({ minLength: 1, maxLength: 40 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    unitPriceFen: MoneyFenSchema,
    costFen: MoneyFenSchema,
    barcode: Type.Union([
      Type.String({ minLength: 4, maxLength: 32, pattern: "^[0-9A-Za-z._-]+$" }),
      Type.Null(),
    ]),
    trackInventory: Type.Boolean(),
    lowStockThreshold: QuantitySchema,
    allowNegativeStock: Type.Boolean(),
    enabled: Type.Boolean(),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ProductUpsertRequest = Static<typeof ProductUpsertRequestSchema>;

export const InventoryOperationTypeSchema = Type.Union([
  Type.Literal("PURCHASE_IN"),
  Type.Literal("OTHER_IN"),
  Type.Literal("RETURN_OUT"),
  Type.Literal("LOSS_OUT"),
  Type.Literal("INTERNAL_USE_OUT"),
  Type.Literal("COUNT_SET"),
  Type.Literal("MANUAL_ADJUSTMENT"),
]);
export type InventoryOperationType = Static<typeof InventoryOperationTypeSchema>;

export const InventoryOperationRequestSchema = Type.Object(
  {
    operationType: InventoryOperationTypeSchema,
    quantity: SignedQuantitySchema,
    unitCostFen: Type.Union([MoneyFenSchema, Type.Null()]),
    referenceNumber: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type InventoryOperationRequest = Static<typeof InventoryOperationRequestSchema>;

export interface InventoryMovementRecord {
  id: string;
  skuId: string;
  productName: string;
  type: InventoryOperationType | "SALE_OUT" | "SALE_RETURN";
  quantityDelta: number;
  quantityBefore: number;
  quantityAfter: number;
  unitCostFen: number | null;
  referenceNumber: string | null;
  reason: string;
  actorId: string;
  authorizerId: string | null;
  sourceEventId: string | null;
  occurredAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export interface InventoryProductRecord {
  skuId: string;
  storeProductCode?: string | null;
  version: number;
  name: string;
  category: string;
  unit: string;
  unitPriceFen: number;
  costFen: number;
  barcode: string | null;
  trackInventory: boolean;
  stockOnHand: number;
  lowStockThreshold: number;
  allowNegativeStock: boolean;
  enabled: boolean;
  lowStock: boolean;
  outOfStock: boolean;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const MemberStatusSchema = Type.Union([
  Type.Literal("ACTIVE"),
  Type.Literal("FROZEN"),
  Type.Literal("DISABLED"),
]);
export type MemberStatus = Static<typeof MemberStatusSchema>;

export const WalletUnitSchema = Type.Union([
  Type.Literal("CNY_FEN"),
  Type.Literal("POINT"),
  Type.Literal("MINUTE"),
  Type.Literal("COUNT"),
  Type.Literal("CUSTOM"),
]);
export type WalletUnit = Static<typeof WalletUnitSchema>;

export const WalletBusinessTypeSchema = Type.Union([
  Type.Literal("STORED_VALUE"),
  Type.Literal("PROMOTIONAL"),
  Type.Literal("LOYALTY"),
  Type.Literal("TIME_CREDIT"),
  Type.Literal("COUNT_CREDIT"),
  Type.Literal("CUSTOM"),
]);
export type WalletBusinessType = Static<typeof WalletBusinessTypeSchema>;

export const WalletPaymentTargetTypeSchema = Type.Union([
  Type.Literal("ORDER"),
  Type.Literal("PRODUCT"),
  Type.Literal("TABLE_TIME"),
  Type.Literal("COACH_SERVICE"),
]);
export type WalletPaymentTargetType = Static<typeof WalletPaymentTargetTypeSchema>;

export const WalletDefinitionUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 40 }),
    unit: WalletUnitSchema,
    customUnitLabel: Type.Union([Type.String({ minLength: 1, maxLength: 12 }), Type.Null()]),
    precision: Type.Integer({ minimum: 0, maximum: 4 }),
    businessType: WalletBusinessTypeSchema,
    refundable: Type.Boolean(),
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type WalletDefinitionUpsertRequest = Static<typeof WalletDefinitionUpsertRequestSchema>;

export interface WalletDefinitionRecord {
  id: string;
  name: string;
  unit: WalletUnit;
  customUnitLabel: string | null;
  precision: number;
  businessType: WalletBusinessType;
  refundable: boolean;
  enabled: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export interface WalletBalanceRecord {
  walletDefinitionId: string;
  walletName: string;
  unit: WalletUnit;
  customUnitLabel: string | null;
  precision: number;
  businessType: WalletBusinessType;
  balance: number;
  version: number;
  enabled: boolean;
}

export const WalletAllocationInputSchema = Type.Object(
  {
    walletDefinitionId: Type.String({ minLength: 1, maxLength: 64 }),
    amount: WalletAmountSchema,
  },
  { additionalProperties: false },
);
export type WalletAllocationInput = Static<typeof WalletAllocationInputSchema>;

export const WalletDisplayGroupUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 40 }),
    unit: WalletUnitSchema,
    presentation: Type.Union([
      Type.Literal("TOTAL_ONLY"),
      Type.Literal("TOTAL_WITH_DETAILS"),
      Type.Literal("DETAILS_ONLY"),
    ]),
    walletDefinitionIds: Type.Array(Type.String({ minLength: 1, maxLength: 64 }), {
      minItems: 1,
      maxItems: 20,
      uniqueItems: true,
    }),
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type WalletDisplayGroupUpsertRequest = Static<typeof WalletDisplayGroupUpsertRequestSchema>;

export interface WalletDisplayGroupRecord {
  id: string;
  name: string;
  unit: WalletUnit;
  presentation: "TOTAL_ONLY" | "TOTAL_WITH_DETAILS" | "DETAILS_ONLY";
  walletDefinitionIds: string[];
  enabled: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const WalletPaymentRuleUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    targetType: WalletPaymentTargetTypeSchema,
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    mode: Type.Union([Type.Literal("PRIORITY"), Type.Literal("STRICT_RATIO")]),
    externalShortfallAllowed: Type.Boolean(),
    components: Type.Array(
      Type.Object(
        {
          walletDefinitionId: Type.String({ minLength: 1, maxLength: 64 }),
          priority: Type.Integer({ minimum: 1, maximum: 100 }),
          ratioBps: Type.Integer({ minimum: 0, maximum: 10_000 }),
          valueFenPerUnit: Type.Integer({ minimum: 1, maximum: 1_000_000 }),
          allowFallback: Type.Boolean(),
        },
        { additionalProperties: false },
      ),
      { minItems: 1, maxItems: 20 },
    ),
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type WalletPaymentRuleUpsertRequest = Static<typeof WalletPaymentRuleUpsertRequestSchema>;

export interface WalletPaymentRuleRecord
  extends Omit<WalletPaymentRuleUpsertRequest, "expectedVersion" | "idempotencyKey"> {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export interface WalletPaymentTargetOption {
  targetType: WalletPaymentTargetType;
  targetId: string | null;
  label: string;
  priceFen: number | null;
  enabled: boolean;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const CreateMemberRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    phone: Type.String({ pattern: "^1[0-9]{10}$" }),
    memberNumber: Type.Union([Type.String({ minLength: 1, maxLength: 32 }), Type.Null()]),
    notes: Type.String({ maxLength: 500 }),
    tags: Type.Array(Type.String({ minLength: 1, maxLength: 24 }), {
      maxItems: 10,
      uniqueItems: true,
    }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateMemberRequest = Static<typeof CreateMemberRequestSchema>;

export const UpdateMemberRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    phone: Type.String({ pattern: "^1[0-9]{10}$" }),
    notes: Type.String({ maxLength: 500 }),
    tags: Type.Array(Type.String({ minLength: 1, maxLength: 24 }), {
      maxItems: 10,
      uniqueItems: true,
    }),
    status: MemberStatusSchema,
    expectedVersion: Type.Integer({ minimum: 1 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type UpdateMemberRequest = Static<typeof UpdateMemberRequestSchema>;

export interface MemberRecord {
  id: string;
  memberNumber: string;
  name: string;
  phoneMasked: string;
  phone?: string;
  status: MemberStatus;
  notes: string;
  tags: string[];
  principalBalanceFen: number;
  bonusBalanceFen: number;
  availableBalanceFen: number;
  totalRechargedFen: number;
  totalBonusFen: number;
  totalConsumedFen: number;
  visitCount: number;
  lastVisitedAt: string | null;
  version: number;
  walletVersion: number;
  wallets?: WalletBalanceRecord[];
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const RechargePlanUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    rechargeAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    bonusAmountFen: Type.Optional(MoneyFenSchema),
    allocations: Type.Optional(
      Type.Array(WalletAllocationInputSchema, { minItems: 1, maxItems: 20 }),
    ),
    description: Type.String({ maxLength: 200 }),
    enabled: Type.Boolean(),
    validFrom: Type.String({ format: "date-time" }),
    validUntil: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RechargePlanUpsertRequest = Static<typeof RechargePlanUpsertRequestSchema>;

export interface RechargePlanRecord {
  id: string;
  name: string;
  rechargeAmountFen: number;
  bonusAmountFen: number;
  allocations?: WalletAllocationInput[];
  description: string;
  enabled: boolean;
  validFrom: string;
  validUntil: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const MemberRechargeRequestSchema = Type.Object(
  {
    planId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    customPrincipalFen: Type.Union([
      Type.Integer({ minimum: 1, maximum: 100_000_000 }),
      Type.Null(),
    ]),
    customAllocations: Type.Optional(
      Type.Union([
        Type.Array(WalletAllocationInputSchema, { minItems: 1, maxItems: 20 }),
        Type.Null(),
      ]),
    ),
    method: Type.Union([
      Type.Literal("CASH"),
      Type.Literal("STATIC_WECHAT"),
      Type.Literal("STATIC_ALIPAY"),
    ]),
    note: Type.String({ maxLength: 200 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MemberRechargeRequest = Static<typeof MemberRechargeRequestSchema>;

export const ReconcileMemberRechargeRequestSchema = Type.Object(
  {
    outcome: Type.Union([
      Type.Literal("CONFIRMED"),
      Type.Literal("NOT_RECEIVED"),
      Type.Literal("AMOUNT_MISMATCH"),
      Type.Literal("DUPLICATE_PAYMENT"),
    ]),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReconcileMemberRechargeRequest = Static<typeof ReconcileMemberRechargeRequestSchema>;

export interface MemberRechargeRecord {
  id: string;
  memberId: string;
  memberName: string;
  planId: string | null;
  paidAmountFen?: number;
  principalFen: number;
  bonusFen: number;
  allocations?: WalletAllocationInput[];
  method: "CASH" | "STATIC_WECHAT" | "STATIC_ALIPAY";
  status:
    | "CONFIRMED_LOCAL"
    | "PENDING_RECONCILIATION"
    | "CONFIRMED_RECONCILED"
    | "RECONCILIATION_EXCEPTION";
  note: string;
  createdAt: string;
  reconciledAt: string | null;
  evidence: "MANUAL_FALLBACK" | "STORE_ACCEPTED";
}

export interface MemberFundTransactionRecord {
  id: string;
  memberId: string;
  type: "RECHARGE" | "CONSUMPTION" | "REFUND" | "ADJUSTMENT";
  principalDeltaFen: number;
  bonusDeltaFen: number;
  principalBalanceFen: number;
  bonusBalanceFen: number;
  entries?: WalletLedgerEntryRecord[];
  paymentRuleId?: string | null;
  paymentRuleVersion?: number | null;
  targetType?: WalletPaymentTargetType | null;
  targetId?: string | null;
  priceSnapshotFen?: number;
  walletCoveredAmountFen?: number;
  externalAmountFen?: number;
  ruleApplications?: WalletRuleApplicationRecord[];
  orderId: string | null;
  rechargeId: string | null;
  originalTransactionId: string | null;
  reason: string;
  actorId: string;
  authorizerId: string | null;
  occurredAt: string;
  evidence: "SIMULATED" | "MANUAL_FALLBACK" | "STORE_ACCEPTED";
}

export interface WalletRuleApplicationRecord {
  targetType: WalletPaymentTargetType;
  targetId: string | null;
  amountFen: number;
  paymentRuleId: string;
  paymentRuleVersion: number;
  walletCoveredAmountFen: number;
  externalAmountFen: number;
}

export const MemberSpendLineSchema = Type.Object(
  {
    targetType: WalletPaymentTargetTypeSchema,
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    amountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
  },
  { additionalProperties: false },
);
export type MemberSpendLine = Static<typeof MemberSpendLineSchema>;

export const MemberSpendRequestSchema = Type.Object(
  {
    memberId: Type.String({ minLength: 1 }),
    orderId: Type.String({ minLength: 1 }),
    amountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    paymentRuleId: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    ),
    targetType: Type.Optional(WalletPaymentTargetTypeSchema),
    targetId: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    ),
    allowExternalShortfall: Type.Optional(Type.Boolean()),
    lines: Type.Optional(Type.Array(MemberSpendLineSchema, { minItems: 1, maxItems: 100 })),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MemberSpendRequest = Static<typeof MemberSpendRequestSchema>;

export interface MemberSpendResult {
  transaction: MemberFundTransactionRecord;
  member: MemberRecord;
  audit?: {
    id: string;
    action: "MEMBER_BALANCE_REFUNDED";
    status: "RECORDED";
  };
}

export interface MemberRefundStatusResult {
  requestId: string;
  status: "NOT_FOUND" | "SUCCEEDED";
  result: MemberSpendResult | null;
}

export const MemberRefundRequestSchema = Type.Object(
  {
    memberId: Type.String({ minLength: 1 }),
    orderId: Type.String({ minLength: 1 }),
    originalTransactionId: Type.String({ minLength: 1 }),
    amountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MemberRefundRequest = Static<typeof MemberRefundRequestSchema>;

export const MemberAdjustmentRequestSchema = Type.Object(
  {
    principalDeltaFen: Type.Optional(SignedMoneyFenSchema),
    bonusDeltaFen: Type.Optional(SignedMoneyFenSchema),
    entries: Type.Optional(
      Type.Array(
        Type.Object(
          {
            walletDefinitionId: Type.String({ minLength: 1, maxLength: 64 }),
            delta: SignedWalletAmountSchema,
          },
          { additionalProperties: false },
        ),
        { minItems: 1, maxItems: 20 },
      ),
    ),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    expectedWalletVersion: Type.Integer({ minimum: 1 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MemberAdjustmentRequest = Static<typeof MemberAdjustmentRequestSchema>;

export interface WalletLedgerEntryRecord {
  id: string;
  transactionId: string;
  walletDefinitionId: string;
  walletName: string;
  unit: WalletUnit;
  customUnitLabel?: string | null;
  precision?: number;
  delta: number;
  balanceBefore: number;
  balanceAfter: number;
  coveredAmountFen: number | null;
  refundableAtTransaction?: boolean;
}

export interface MemberDashboard {
  activeMembers: number;
  frozenMembers: number;
  totalAvailableBalanceFen: number;
  pendingRechargeCount: number;
  todayRechargeFen: number;
  todayConsumptionFen: number;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const GroupBuyProviderSchema = Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]);
export type GroupBuyProvider = Static<typeof GroupBuyProviderSchema>;

export const GroupBuyChannelUpsertRequestSchema = Type.Object(
  {
    provider: GroupBuyProviderSchema,
    displayName: Type.String({ minLength: 1, maxLength: 40 }),
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type GroupBuyChannelUpsertRequest = Static<typeof GroupBuyChannelUpsertRequestSchema>;

export interface GroupBuyChannelRecord {
  id: string;
  provider: GroupBuyProvider;
  displayName: string;
  fulfillmentMode: "MANUAL_PLATFORM_APP";
  officialIntegration: "NOT_VERIFIED";
  enabled: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "MANUAL_FALLBACK";
}

export const GroupBuyPackageComponentSchema = Type.Object(
  {
    kind: Type.Union([Type.Literal("PRODUCT"), Type.Literal("SERVICE")]),
    referenceId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
  },
  { additionalProperties: false },
);
export type GroupBuyPackageComponent = Static<typeof GroupBuyPackageComponentSchema>;

export const GroupBuyPackageUpsertRequestSchema = Type.Object(
  {
    channelId: Type.String({ minLength: 1, maxLength: 64 }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    includedMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
    components: Type.Array(GroupBuyPackageComponentSchema, { maxItems: 30 }),
    voucherCoveredFen: MoneyFenSchema,
    customerSupplementFen: MoneyFenSchema,
    expectedSettlementFen: MoneyFenSchema,
    expectedFeeFen: MoneyFenSchema,
    expectedSubsidyFen: MoneyFenSchema,
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type GroupBuyPackageUpsertRequest = Static<typeof GroupBuyPackageUpsertRequestSchema>;

export interface GroupBuyPackageRecord
  extends Omit<GroupBuyPackageUpsertRequest, "expectedVersion" | "idempotencyKey"> {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const RecordGroupBuyFulfillmentRequestSchema = Type.Object(
  {
    sessionId: Type.String({ minLength: 1, maxLength: 128 }),
    channelId: Type.String({ minLength: 1, maxLength: 64 }),
    packageId: Type.String({ minLength: 1, maxLength: 64 }),
    platformReference: Type.String({ minLength: 4, maxLength: 64 }),
    voucherCoveredFen: MoneyFenSchema,
    customerSupplementFen: MoneyFenSchema,
    expectedSettlementFen: MoneyFenSchema,
    expectedFeeFen: MoneyFenSchema,
    expectedSubsidyFen: MoneyFenSchema,
    note: Type.String({ maxLength: 200 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RecordGroupBuyFulfillmentRequest = Static<
  typeof RecordGroupBuyFulfillmentRequestSchema
>;

export const ReconcileGroupBuyFulfillmentRequestSchema = Type.Object(
  {
    outcome: Type.Union([
      Type.Literal("CONFIRMED"),
      Type.Literal("NOT_SETTLED"),
      Type.Literal("AMOUNT_MISMATCH"),
      Type.Literal("DUPLICATE_RECORD"),
    ]),
    actualSettlementFen: MoneyFenSchema,
    actualFeeFen: MoneyFenSchema,
    actualSubsidyFen: MoneyFenSchema,
    note: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReconcileGroupBuyFulfillmentRequest = Static<
  typeof ReconcileGroupBuyFulfillmentRequestSchema
>;

export interface GroupBuyFulfillmentRecord {
  id: string;
  orderId: string;
  sessionId: string;
  tableId: string;
  tableNumber: string;
  businessDate: string;
  channelSnapshot: Pick<
    GroupBuyChannelRecord,
    "id" | "provider" | "displayName" | "version" | "fulfillmentMode" | "officialIntegration"
  >;
  packageSnapshot: Pick<
    GroupBuyPackageRecord,
    "id" | "name" | "version" | "includedMinutes" | "components"
  >;
  platformReference: string;
  voucherCoveredFen: number;
  customerSupplementFen: number;
  expectedSettlementFen: number;
  expectedFeeFen: number;
  expectedSubsidyFen: number;
  actualSettlementFen: number | null;
  actualFeeFen: number | null;
  actualSubsidyFen: number | null;
  reconciliationOutcome:
    | "PENDING"
    | "CONFIRMED"
    | "NOT_SETTLED"
    | "AMOUNT_MISMATCH"
    | "DUPLICATE_RECORD";
  note: string;
  reconciliationNote: string | null;
  coverageStartedAt: string;
  coverageEndsAt: string;
  fulfilledAt: string;
  reconciledAt: string | null;
  actorId: string;
  authorizerId: string | null;
  evidence: "MANUAL_FALLBACK";
}

export interface GroupBuyChannelReconciliationRow {
  provider: GroupBuyProvider;
  channelName: string;
  fulfillmentCount: number;
  orderCount: number;
  voucherCoveredFen: number;
  customerSupplementFen: number;
  expectedSettlementFen: number;
  actualSettlementFen: number;
  expectedFeeFen: number;
  actualFeeFen: number;
  expectedSubsidyFen: number;
  actualSubsidyFen: number;
  pendingCount: number;
  exceptionCount: number;
}

export interface GroupBuyReconciliationSummary {
  businessDate: string;
  channels: GroupBuyChannelReconciliationRow[];
  evidence: "MANUAL_FALLBACK";
  officialIntegration: "NOT_VERIFIED";
}
